import { apiService } from './api';
import type { Game, GameFilters, GamesApiResponse, GameApiResponse, GameSection, Team, PersonalizedCatalogResponse } from '../types/game';

/**
 * Games Service
 * Handles all game-related API calls
 */
class GamesService {
  /**
   * Get personalized game catalog (authenticated)
   * @param filters - Optional filters (category, tags, search)
   * @param page - Page number (default 1)
   * @param limit - Items per page (default 20)
   * @returns Promise with games array, sections, teams, and team games
   */
  async getPersonalizedCatalog(
    filters: GameFilters = {},
    page: number = 1,
    limit: number = 20
  ): Promise<{ 
    games: Game[]; 
    sections?: GameSection[];
    privateGames?: Game[];
    unpublishedGames?: Game[];
    userTeams?: Team[];
    teamGames?: { [teamId: string]: Game[] };
  }> {
    try {
      const params = new URLSearchParams();
      
      if (filters.category && filters.category !== 'All') {
        params.append('category', filters.category);
      }
      if (filters.search) {
        params.append('search', filters.search);
      }
      if (filters.tags && filters.tags.length > 0) {
        filters.tags.forEach(tag => params.append('tags', tag));
      }
      if (filters.featured) {
        params.append('featured', 'true');
      }
      
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      
      const queryString = params.toString();
      const url = `/games/catalog/personalized${queryString ? `?${queryString}` : ''}`;
      
      const response = await apiService.get<PersonalizedCatalogResponse>(url);
      
      if (response.data.success && response.data.data) {
        const data = response.data.data;
        
        if (Array.isArray(data)) {
          // Legacy format - just array of games
          return { games: data };
        }
        
        // New format with sections, teams, and team games
        const games = data.games || data.catalogGames || [];
        const sections = data.sections;
        const privateGames = data.privateGames;
        const unpublishedGames = data.unpublishedGames;
        const userTeams = data.userTeams;
        const teamGames = data.teamGames;
        
        return { games, sections, privateGames, unpublishedGames, userTeams, teamGames };
      }
      
      console.warn('[GamesService] Unexpected response format:', response.data);
      return { games: [] };
    } catch (error) {
      console.error('[GamesService] Failed to fetch personalized catalog:', error);
      // Fallback to public catalog if personalized fails (e.g., not authenticated)
      const games = await this.getPublicCatalog(filters, page, limit);
      return { games };
    }
  }

  /**
   * Get public game catalog (fallback for unauthenticated users)
   * @param filters - Optional filters (category, tags, search)
   * @param page - Page number (default 1)
   * @param limit - Items per page (default 20)
   */
  async getPublicCatalog(
    filters: GameFilters = {},
    page: number = 1,
    limit: number = 20
  ): Promise<Game[]> {
    try {
      const params = new URLSearchParams();
      
      if (filters.category && filters.category !== 'All') {
        params.append('category', filters.category);
      }
      if (filters.search) {
        params.append('search', filters.search);
      }
      if (filters.tags && filters.tags.length > 0) {
        filters.tags.forEach(tag => params.append('tags', tag));
      }
      if (filters.featured) {
        params.append('featured', 'true');
      }
      
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      
      const queryString = params.toString();
      const url = `/games/catalog${queryString ? `?${queryString}` : ''}`;
      
      const response = await apiService.get<GamesApiResponse>(url);
      
      if (response.data.success && Array.isArray(response.data.data)) {
        return response.data.data;
      }
      
      console.warn('[GamesService] Unexpected response format:', response.data);
      return [];
    } catch (error) {
      console.error('[GamesService] Failed to fetch public catalog:', error);
      throw error;
    }
  }

  /**
   * Get game catalog (uses personalized if available, falls back to public)
   * @param filters - Optional filters (category, tags, search)
   * @param page - Page number (default 1)
   * @param limit - Items per page (default 20)
   */
  async getGameCatalog(
    filters: GameFilters = {},
    page: number = 1,
    limit: number = 20
  ): Promise<Game[]> {
    // Try personalized catalog first (requires authentication)
    const result = await this.getPersonalizedCatalog(filters, page, limit);
    return result.games;
  }

  /**
   * Get game by ID
   * @param gameId - Game UUID
   */
  async getGameById(gameId: string): Promise<Game | null> {
    try {
      const response = await apiService.get<GameApiResponse>(`/games/${gameId}`);
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      
      return null;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      console.error('[GamesService] Failed to fetch game:', error);
      throw error;
    }
  }

  /**
   * Search games by query
   * @param query - Search query string
   * @param limit - Max results (default 20)
   */
  async searchGames(query: string, limit: number = 20): Promise<Game[]> {
    if (!query.trim()) {
      const result = await this.getPersonalizedCatalog({}, 1, limit);
      return result.games;
    }
    
    const result = await this.getPersonalizedCatalog({ search: query }, 1, limit);
    return result.games;
  }

  /**
   * Get games by category
   * @param category - Category ID
   * @param limit - Max results (default 20)
   */
  async getGamesByCategory(category: string, limit: number = 20): Promise<Game[]> {
    if (category === 'All') {
      const result = await this.getPersonalizedCatalog({}, 1, limit);
      return result.games;
    }
    
    const result = await this.getPersonalizedCatalog({ category }, 1, limit);
    return result.games;
  }

  /**
   * Get featured games (top by plays)
   * Uses server sections if available, otherwise client-side sorting
   * @param limit - Max results (default 4)
   */
  async getFeaturedGames(limit: number = 4): Promise<Game[]> {
    const result = await this.getPersonalizedCatalog({}, 1, 50);
    
    // Use server sections if available
    if (result.sections) {
      const featuredSection = result.sections.find(s => s.type === 'featured');
      if (featuredSection) {
        return featuredSection.games.slice(0, limit);
      }
    }
    
    // Fallback to client-side sorting by total_plays
    return [...result.games]
      .sort((a, b) => (b.total_plays || 0) - (a.total_plays || 0))
      .slice(0, limit);
  }

  /**
   * Get trending games (highest engagement)
   * Uses server sections if available, otherwise client-side sorting
   * @param limit - Max results (default 4)
   */
  async getTrendingGames(limit: number = 4): Promise<Game[]> {
    const result = await this.getPersonalizedCatalog({}, 1, 50);
    
    // Use server sections if available
    if (result.sections) {
      const trendingSection = result.sections.find(s => s.type === 'trending');
      if (trendingSection) {
        return trendingSection.games.slice(0, limit);
      }
    }
    
    // Fallback to client-side sorting by engagement score
    return [...result.games]
      .sort((a, b) => {
        const scoreA = (a.total_plays || 0) + (a.total_likes || 0) * 10;
        const scoreB = (b.total_plays || 0) + (b.total_likes || 0) * 10;
        return scoreB - scoreA;
      })
      .slice(0, limit);
  }

  /**
   * Get recently added games
   * Uses server sections if available, otherwise client-side sorting
   * @param limit - Max results (default 4)
   */
  async getRecentGames(limit: number = 4): Promise<Game[]> {
    const result = await this.getPersonalizedCatalog({}, 1, 50);
    
    // Use server sections if available
    if (result.sections) {
      const newSection = result.sections.find(s => s.type === 'new');
      if (newSection) {
        return newSection.games.slice(0, limit);
      }
    }
    
    // Fallback to client-side sorting by created_at
    return [...result.games]
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, limit);
  }
}

export const gamesService = new GamesService();
export default gamesService;

